import { useEffect, useState } from "react";
import {
  fetchVehicleModelCopyHistory,
  fetchVehicleModelCopyHistoryVersion,
  restoreVehicleModelCopyVersion,
} from "../../../../services/vehicleModelCopyAdminApi";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function eventLabel(eventType) {
  return String(eventType || "unknown").replaceAll("_", " ");
}

const VehicleModelCopyHistoryPanel = ({
  copyId,
  canRestore,
  expectedUpdatedAt,
  onRestored,
}) => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, offset: 0 });
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const loadHistory = async () => {
    if (!copyId) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleModelCopyHistory(copyId, pagination);
      setItems(data.items);
      setPagination(data.pagination);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load history.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [copyId, pagination.offset, pagination.limit]);

  const loadVersion = async (versionId) => {
    try {
      setSelectedVersionId(versionId);
      const version = await fetchVehicleModelCopyHistoryVersion(copyId, versionId);
      setSelectedVersion(version);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load historical version.");
      setSelectedVersion(null);
    }
  };

  const handleRestore = async () => {
    if (!canRestore || !selectedVersionId) return;
    const proceed = window.confirm(
      "Restore this historical version as a new draft? Published content will remain unchanged until you publish again."
    );
    if (!proceed) return;
    try {
      setRestoring(true);
      setErrorMessage("");
      await restoreVehicleModelCopyVersion(copyId, selectedVersionId, {
        expectedUpdatedAt,
      });
      setToast("Historical version restored as draft.");
      onRestored?.();
      await loadHistory();
    } catch (error) {
      if (error.status === 409) {
        setErrorMessage(
          "Restore conflict: this copy changed after you opened history. Reload and try again."
        );
      } else {
        setErrorMessage(error.message || "Restore failed.");
      }
    } finally {
      setRestoring(false);
    }
  };

  if (!copyId) {
    return <p className="muted">Save a draft for this locale to start history tracking.</p>;
  }

  return (
    <section className="admin-vehicle-model-copy-history" aria-label="Copy history">
      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="admin-state-panel admin-state-panel--error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {loading ? <p className="muted">Loading history…</p> : null}

      {!loading ? (
        <div className="admin-vehicle-model-copy-history__layout">
          <div>
            <h3>Versions</h3>
            <ul className="admin-vehicle-model-copy-history__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`admin-vehicle-model-copy-history__item${
                      selectedVersionId === item.id
                        ? " admin-vehicle-model-copy-history__item--active"
                        : ""
                    }`}
                    onClick={() => loadVersion(item.id)}
                  >
                    <strong>v{item.versionNumber}</strong> · {eventLabel(item.eventType)}
                    <span className="muted">
                      {formatDate(item.createdAt)} · {item.actor?.displayName || "system"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="admin-vehicle-model-copy-history__pager">
              <button
                type="button"
                className="admin-dashboard__action-btn"
                disabled={pagination.offset <= 0}
                onClick={() =>
                  setPagination((current) => ({
                    ...current,
                    offset: Math.max(0, current.offset - current.limit),
                  }))
                }
              >
                Previous
              </button>
              <button
                type="button"
                className="admin-dashboard__action-btn"
                disabled={pagination.offset + pagination.limit >= pagination.total}
                onClick={() =>
                  setPagination((current) => ({
                    ...current,
                    offset: current.offset + current.limit,
                  }))
                }
              >
                Next
              </button>
            </div>
          </div>

          <div>
            <h3>Version detail</h3>
            {!selectedVersion ? (
              <p className="muted">Select a version to compare and restore.</p>
            ) : (
              <>
                <p>
                  <strong>Historical (not live)</strong> · {selectedVersion.status} ·{" "}
                  {formatDate(selectedVersion.createdAt)}
                </p>
                <pre className="admin-vehicle-model-copy-editor__readonly">{selectedVersion.body}</pre>
                <h4>Compare with current draft</h4>
                <pre className="admin-vehicle-model-copy-editor__readonly">
                  {selectedVersion.currentDraft || "—"}
                </pre>
                <h4>Compare with current published</h4>
                <pre className="admin-vehicle-model-copy-editor__readonly">
                  {selectedVersion.currentPublished || "—"}
                </pre>
                {canRestore ? (
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    disabled={restoring}
                    onClick={handleRestore}
                  >
                    {restoring ? "Restoring…" : "Restore as draft"}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default VehicleModelCopyHistoryPanel;
